import type { QueryClient } from 'react-query';
import appConfig from '@frontend/env';
import { SchemaWeTagsService } from '@frontend/schema';
import { type WetagQuery, getQueryCacheKey } from './query-builder';
import type { WetagQueryResponse, WetagClientConfig } from './types';

let config: WetagClientConfig | null = null;
let storedQueryClient: QueryClient | null = null;

export function initializeWetagClient(clientConfig: WetagClientConfig = {}): void {
  config = clientConfig;
}

export function getWetagConfig(): WetagClientConfig {
  return config || {};
}

export function isWetagClientInitialized(): boolean {
  return config !== null;
}

export function setWetagQueryClient(queryClient: QueryClient): void {
  storedQueryClient = queryClient;
}

export function getStoredQueryClient(): QueryClient {
  if (!storedQueryClient) {
    throw new Error('QueryClient not set. Call setWetagQueryClient(queryClient) during app initialization.');
  }
  return storedQueryClient;
}

export function getStoredQueryClientOrUndefined(): QueryClient | undefined {
  return storedQueryClient ?? undefined;
}

export interface ExecuteWetagQueryOptions {
  namespaceId: string;
  query: WetagQuery;
  entityId: string | undefined;
}

export async function executeWetagQuery(options: ExecuteWetagQueryOptions): Promise<WetagQueryResponse> {
  const { namespaceId, query, entityId } = options;

  const queryString = query._toQueryString(entityId ?? '');

  try {
    const response = await SchemaWeTagsService.QueryNamespace(
      {
        namespaceId,
        query: queryString,
      },
      { baseUrl: appConfig.BACKEND_API_V2 }
    );

    return response;
  } catch (error) {
    if (error && typeof error === 'object' && 'status' in error) {
      const status = (error as { status: number }).status;
      if (status === 404) {
        console.warn('Wetag not found. Are you sure you created it?');
        return { result: false };
      }
    }
    return { result: false };
  }
}

const baseKey = 'wetags';

export const wetagQueryKeys = {
  all: [baseKey] as const,
  namespace: (namespaceId: string) => [baseKey, namespaceId] as const,
  entity: (namespaceId: string, entityId: string) => [baseKey, namespaceId, entityId] as const,
  query: (namespaceId: string, entityId: string, query: WetagQuery) =>
    [baseKey, namespaceId, entityId, getQueryCacheKey(query)] as const,
  tag: (namespaceId: string, tagId: string, entityId: string) => [baseKey, namespaceId, tagId, entityId] as const,
};
